// Pure CMS reporting-window + in-flight logic for the patient-drawer CMS card.
// No React, no I/O. Windows are computed CMS-true (Operations Manual), NOT from
// the stored q*/final_due_date columns — those use a fixed cadence that diverges
// from the rolling rule (see docs/specs/2026-06-30-cms-drawer-quick-actions.md §4).

export interface CmsCarePeriod {
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  cms_alignment_date?: string | null;
}

export interface CmsSubmissionLike {
  operation: string;
  status: string;
  result_code?: string | null;
  submitted_at?: string | null;
  finished_at?: string | null;
  created?: string | null; // apiv2 creation ts (ISO) — the field apiv2 actually returns
  created_at?: string | null; // contract-spec alias; absent on apiv2 reads
  // WF37 stamps report_kind here — no dedicated column exists (see milestoneReportDates).
  request_payload?: { report_kind?: string | null; [k: string]: unknown } | null;
}

export type CmsReportKind = 'baseline' | 'quarterly' | 'final';

// apiv2 `cms_submissions.operation` literals.
export const CMS_OPERATION = {
  eligibility: 'check_eligibility',
  alignment: 'align',
  reporting: 'report_data',
  unalign: 'unalign',
} as const;

export interface CmsWindow { openAt: number; closeAt: number }

const DAY = 86_400_000;
const FLIGHT = new Set(['draft', 'submitted', 'polling']);

function ms(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

// CMS "from the prior submission" = when CMS accepted it; finished_at can be null
// on legacy rows, so fall back to submitted_at then created_at.
function reportAnchorMs(r: CmsSubmissionLike): number | null {
  return ms(r.finished_at) ?? ms(r.submitted_at) ?? ms(r.created) ?? ms(r.created_at);
}

// WF37 writes status='success' for both `success` and `duplicate` result codes
// (result_code carries the distinction), so status==='success' alone is sufficient.
function isReportSuccess(r: CmsSubmissionLike): boolean {
  return r.operation === CMS_OPERATION.reporting && r.status === 'success';
}

/** Successful report_data submissions, newest first. CMS runs NO server-side dedupe on
 *  $report-data — a re-submit for the same period is accepted as a fresh `success` with a new
 *  submission id (observed on IMPL 2026-08-07: rows 354 and 357, minutes apart, both success).
 *  So nothing but the operator stops the same report being filed twice. */
export function successfulReports(
  subs: readonly CmsSubmissionLike[] | undefined | null,
): CmsSubmissionLike[] {
  const ok = (subs || []).filter(isReportSuccess);
  ok.sort((a, b) => (reportAnchorMs(b) ?? 0) - (reportAnchorMs(a) ?? 0));
  return ok;
}

export function lastSuccessfulReport(
  subs: readonly CmsSubmissionLike[] | undefined | null,
): CmsSubmissionLike | null {
  return successfulReports(subs)[0] || null;
}

// Any non-terminal submission of `operation` (gates the button — mirrors the DB
// single-in-flight unique index per (care_period, operation)).
export function inFlight(
  subs: readonly CmsSubmissionLike[] | undefined | null,
  operation: string,
): boolean {
  return (subs || []).some((r) => r.operation === operation && FLIGHT.has(r.status));
}

// Baseline: within 60 days of alignment (opens at alignment). Null when not yet aligned.
export function baselineWindow(cp: CmsCarePeriod | null | undefined): CmsWindow | null {
  const a = ms(cp?.cms_alignment_date);
  if (a == null) return null;
  return { openAt: a, closeAt: a + 60 * DAY };
}

// Quarterly: rolling 70–110 days from the prior successful report (baseline counts
// as the first prior). Null when no successful report yet (baseline must land first).
export function quarterlyWindow(
  cp: CmsCarePeriod | null | undefined,
  subs: readonly CmsSubmissionLike[] | undefined | null,
): CmsWindow | null {
  const last = lastSuccessfulReport(subs);
  const anchor = last ? reportAnchorMs(last) : null;
  if (anchor == null) return null;
  return { openAt: anchor + 70 * DAY, closeAt: anchor + 110 * DAY };
}

// End-of-period: by day 425 (open from period end, day 365). Null when not aligned.
export function finalWindow(cp: CmsCarePeriod | null | undefined): CmsWindow | null {
  const a = ms(cp?.cms_alignment_date);
  if (a == null) return null;
  return { openAt: a + 365 * DAY, closeAt: a + 425 * DAY };
}

export function isInWindow(w: CmsWindow | null, now: number): boolean {
  return !!w && now >= w.openAt && now <= w.closeAt;
}

/** Shared display format for CMS dates (window hints, milestone tooltip, "Submitted to CMS").
 *  Locale pinned: the back-office is mono-language English (browser-locale dates rendered
 *  "3 août" next to English labels on a French browser). */
export function formatReportDate(t: number): string {
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const fmt = formatReportDate;

export interface ReportAction {
  kind: CmsReportKind;
  label: string;
  enabled: boolean;
  reason: string | null; // disabled-state explanation (tooltip + hint), null when enabled
}

// The three active-state reporting buttons, with per-window enable + reason.
// `now` is injected so the logic stays pure and testable.
export function reportingActions(
  cp: CmsCarePeriod | null | undefined,
  subs: readonly CmsSubmissionLike[] | undefined | null,
  now: number,
): ReportAction[] {
  const busy = inFlight(subs, CMS_OPERATION.reporting);
  const hasBaseline = !!lastSuccessfulReport(subs);

  const windowReason = (w: CmsWindow | null, missingMsg: string): string | null => {
    if (!w) return missingMsg;
    if (now < w.openAt) return `Opens ${fmt(w.openAt)}`;
    if (now > w.closeAt) return 'Window closed';
    return null;
  };

  const build = (kind: CmsReportKind, label: string, w: CmsWindow | null, missingMsg: string): ReportAction => {
    let reason: string | null = null;
    if (busy) reason = 'A submission is already in flight';
    else reason = windowReason(w, missingMsg);
    return {
      kind, label, enabled: reason == null, reason,
    };
  };

  return [
    build('baseline', 'Submit baseline', baselineWindow(cp), 'Awaiting CMS alignment'),
    build(
      'quarterly',
      'Submit quarterly',
      quarterlyWindow(cp, subs),
      hasBaseline ? 'No quarterly window open' : 'Baseline not yet submitted to CMS',
    ),
    build('final', 'Submit final', finalWindow(cp), 'Awaiting CMS alignment'),
  ];
}

export type CmsMilestoneSlot = 'baseline' | 'q1' | 'q2' | 'q3' | 'final';

/** Latest successful submission date (epoch ms) per reporting milestone, or null. */
export type MilestoneReportDates = Record<CmsMilestoneSlot, number | null>;

const Q_SLOTS: CmsMilestoneSlot[] = ['q1', 'q2', 'q3'];

function kindOf(r: CmsSubmissionLike): 'baseline' | 'quarterly' | 'final' | 'unknown' | null {
  const k = r.request_payload?.report_kind;
  if (k == null) return null; // pre-stamp legacy row → positional fallback
  if (k === 'baseline' || k === 'final') return k;
  if (typeof k === 'string' && k.startsWith('quarterly')) return 'quarterly';
  return 'unknown'; // a future kind — excluded rather than guessed into a slot
}

// Successes for the SAME period can repeat (no CMS dedupe — a re-file is a fresh success),
// and quarterlyWindow says the next period's window can't even open until 70 days after the
// latest prior success. So within one milestone stream, a success < 70d after the current
// slot's latest anchor is a re-file of that slot (keep the latest — that's the displayed
// date); >= 70d starts the next slot.
function collapseIntoSlots(anchors: number[]): number[] {
  return anchors.reduce<number[]>((slots, at) => {
    const last = slots[slots.length - 1];
    if (last === undefined || at >= last + 70 * DAY) return [...slots, at];
    return [...slots.slice(0, -1), Math.max(last, at)];
  }, []);
}

/** Latest successful report date per milestone (baseline / Q1–Q3 / final).
 *  Attribution reads `request_payload.report_kind` (stamped by WF37 since 2026-08-16, S-103);
 *  quarterlies fill Q1→Q3 in submission order. Legacy rows without the stamp run through
 *  the same slot collapse positionally (first stream slot = baseline, then Q1…), which is
 *  exact for the live data — nothing but baselines has ever been filed. An unrecognized
 *  stamped kind is excluded entirely, never guessed into a slot. Overflow past the last
 *  quarterly slot keeps the latest date in Q3 rather than inventing a fourth quarter. */
export function milestoneReportDates(
  subs: readonly CmsSubmissionLike[] | undefined | null,
): MilestoneReportDates {
  const out: MilestoneReportDates = {
    baseline: null, q1: null, q2: null, q3: null, final: null,
  };
  const asc = successfulReports(subs)
    .map((r) => ({ kind: kindOf(r), at: reportAnchorMs(r) }))
    .filter((r): r is { kind: ReturnType<typeof kindOf>; at: number } => r.at != null)
    .reverse();

  const latest = (slot: CmsMilestoneSlot, at: number) => {
    out[slot] = out[slot] == null ? at : Math.max(out[slot], at);
  };
  const fillQ = (anchors: number[], slots: CmsMilestoneSlot[]) => {
    collapseIntoSlots(anchors).forEach((at, i) => {
      const slot = slots[Math.min(i, slots.length - 1)];
      if (slot) latest(slot, at);
    });
  };

  fillQ(asc.filter((r) => r.kind === 'quarterly').map((r) => r.at), Q_SLOTS);
  asc.forEach((r) => {
    if (r.kind === 'baseline' || r.kind === 'final') latest(r.kind, r.at);
  });
  // Legacy stream: positional — slot 0 is the baseline, then the quarterlies.
  fillQ(asc.filter((r) => r.kind == null).map((r) => r.at), ['baseline', ...Q_SLOTS]);
  return out;
}
